<template>
  <div class="chart-container">
    <div class="chart-scroll-wrapper">
      <canvas ref="chartCanvas" class="chart-canvas"></canvas>
    </div>
  </div>
</template>

<script>
import { Bar } from 'chart.js';

// chart.js 2.x writes into the data it is given (`_meta` on every dataset, and it patches push/pop/splice on every `data`
// array), so it gets a copy: arrays and plain objects are cloned, everything else (strings, numbers, null, functions) is kept.
function cloneChartData(value) {
  if (Array.isArray(value)) return value.map(cloneChartData);
  if (value && Object.getPrototypeOf(value) === Object.prototype) {
    const out = {};
    Object.keys(value).forEach((k) => { out[k] = cloneChartData(value[k]); });
    return out;
  }
  return value;
}

export default {
  name: 'ChartJSBarChart',
  props: {
    chartData: {
      type: Object,
      required: true,
      default: () => ({
        labels: [],
        datasets: [],
      }),
    },
    options: {
      type: Object,
      default: () => ({}), // Default to an empty object
    },
    dateRange: {
      type: String,
      required: false,
    },
    timeRange: {
      type: String,
      required: false,
    },
  },
  created() {
    // Held outside `data` on purpose: the chart instance must not be made reactive.
    this.chart = null;
  },
  beforeDestroy() {
    this.destroyChart();
  },
  methods: {
    // A chart that is not destroyed stays alive (resize listener, its data, its canvas) and a redraw stacks another one on top.
    destroyChart() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
    },
    createChart() {
      if (!this.chartData || !this.chartData.datasets) return;
      // A redraw queued on nextTick can run after the component is gone.
      if (this._isDestroyed || !this.$refs.chartCanvas) return;

      this.destroyChart();
      const ctx = this.$refs.chartCanvas.getContext('2d');

      const defaultOptions = {
          responsive: true,
          maintainAspectRatio: false, // Allow the chart to stretch
          hover: {
            animationDuration: 0, // Disable hover animation
            mode: 'nearest', // Only highlight the nearest item
            intersect: true // Only trigger on intersect
          },
          animation: {
            duration: 0 // Disable general animations
          },
          scales: {
            xAxes: [
              {
                gridLines: {
                  display: false, // Hide grid lines for the x-axis
                },
                ticks: {
                  autoSkip: false, // Ensure all labels are shown
                  //maxRotation: 45, // Rotate labels for better visibility
                  //minRotation: 45,
                },
                // Adjust bar thickness and spacing
                categoryPercentage: 0.6, // Controls the width of the category (0 to 1)
                barPercentage: 0.8, // Controls the width of the bars within the category (0 to 1)
              },
            ],
            yAxes: [
              {
                gridLines: {
                  display: false, // Hide grid lines for the y-axis
                },
                ticks: {
                  beginAtZero: true, // Start the y-axis at 0
                },
              },
            ],
          },
          legend: {
            display: true,
            position: 'bottom',
            labels: {
              boxWidth: 15,
              padding: 15,
              usePointStyle: true,
            },
          },
          tooltips: {
            enabled: true,
            mode: 'nearest', // Show tooltip for the nearest bar
            intersect: true, // Only show tooltip when the mouse intersects a bar
            callbacks: {
              label: function (tooltipItem, data) {
                const datasetLabel = data.datasets[tooltipItem.datasetIndex].label || '';
                return `${datasetLabel}: ${tooltipItem.yLabel}`;
              },
            },
          },
          title: {
            display: true,
            text: this.dateRange || '',
            padding: 10,
          },
      };

      // Merge default options with custom options
      const mergedOptions = { ...defaultOptions, ...this.options };

      this.chart = new Bar(ctx, {
        type: 'bar',
        data: cloneChartData(this.chartData),
        options: mergedOptions
      });
    },
  },
  mounted() {
    if (this.chartData && this.chartData.datasets && this.chartData.datasets.length > 0) {
      this.createChart();
    }
  },
  watch: {
    chartData: {
      handler(newVal) {
        if (newVal && newVal.datasets && newVal.datasets.length > 0) {
          this.$nextTick(() => {
            this.createChart();
          });
        }
      },
      deep: true,
    },
  },
};
</script>

<style scoped>
.chart-container {
  width: 100%;
  overflow-x: auto; /* Enable horizontal scrolling */
}

.chart-scroll-wrapper {
  min-width: 350px; /* Adjust based on the number of data points */
  position: relative;
  height: 200px; /* Set a fixed height for the chart */
}

.chart-canvas {
  width: 100% !important; /* Ensure the chart takes up the full width */
  height: 100% !important; /* Ensure the chart takes up the full height */
}
</style>